// src/services/adminService.js
import {
  ADMIN_STATS_API,
  ADMIN_USER_DATA_API,
  ANALYTICS_API,
  USER_API,
} from "../api/apiroutes.js";
import axiosInstance from "../api/axiosInstance.js";

import { toast } from "react-toastify";

const handleToast = (success, message) => {
  if (success) toast.success(message);
  else toast.error(message);
};

const AdminService = {
  async suspendUser(userId, setSelectedUser = null) {
    try {
      const { data } = await axiosInstance.patch(USER_API.SUSPEND_USER(userId));
      handleToast(true, data.message || "User suspended successfully");
      if (setSelectedUser)
        setSelectedUser((prev) => ({ ...prev, issuspended: true }));
      return data;
    } catch (err) {
      handleToast(
        false,
        err.response?.data?.message || "Failed to suspend user",
      );
      throw err;
    }
  },

  async unsuspendUser(userId, setSelectedUser = null) {
    try {
      const { data } = await axiosInstance.patch(
        USER_API.UNSUSPEND_USER(userId),
      );
      handleToast(true, data.message || "User unsuspended successfully");
      if (setSelectedUser)
        setSelectedUser((prev) => ({ ...prev, issuspended: false }));
      return data;
    } catch (err) {
      handleToast(
        false,
        err.response?.data?.message || "Failed to unsuspend user",
      );
      throw err;
    }
  },

  /**
   * Abuse Report → Force Disconnect. Removes the user's Tapo connection and
   * linked data even when Tapo's own unlink fails.
   *
   * Deliberately NOT toasted here: the page picks success vs. info from
   * `providerDisconnect`, and shows a failure inside the open dialog.
   */
  async forceDisconnectTapo(userId, accountId) {
    const { data } = await axiosInstance.post(
      USER_API.FORCE_DISCONNECT_TAPO(userId),
      { accountId },
    );
    return data;
  },

  async fetchDeviceStats(clientType = "web") {
    try {
      const res = await axiosInstance.get(ADMIN_STATS_API.DEVICE_STATS, {
        headers: { "x-client-type": clientType },
      });

      // Normalize empty array to default object
      return Array.isArray(res.data.data) && res.data.data.length === 0
        ? { totalDevices: 0, byType: {}, online: 0, offline: 0, unknown: 0 }
        : res.data.data;
    } catch (err) {
      console.error("Failed to fetch device stats", err);
      return { totalDevices: 0, byType: {}, online: 0, offline: 0, unknown: 0 };
    }
  },

  async fetchApiUsageMonitoring({ endpoint, minHits = 3, date }) {
    const params = {
      endpoint,
      minHits,
    };

    if (date) params.date = date;

    const res = await axiosInstance.get(
      `${ANALYTICS_API.USAGE_MONITORING}`,
      { params },
    );

    return res.data;
  },

  async extendTrial(userId, extraDays) {
    try {
      const { data } = await axiosInstance.post(USER_API.EXTEND_TRIAL, {
        userId,
        extraDays,
      });
      handleToast(true, data.message || "Trial extended successfully");
      return data;
    } catch (err) {
      handleToast(
        false,
        err.response?.data?.message || "Failed to extend trial",
      );
      throw err;
    }
  },

  async promoteUser(userId, role) {
  console.log(userId, "userid >>>>");

  try {
    let response;

    if (role === "subscriber") {
      // DELETE request
      response = await axiosInstance.delete(USER_API.PROMOTE_USER, {
        data: { userId }
      });
    } else {
      // POST request
      response = await axiosInstance.post(USER_API.PROMOTE_USER, {
        userId,
        role,
      });
    }

    const data = response.data;

    handleToast(true, data.message || `User successfully updated to ${role}`);

    return data;

  } catch (err) {
    console.log(err, "error >>>>");

    // Correct status check
    // if (err.response?.status === 403) {
    //   handleToast(false, "You don't have permission to perform this action");
    // } else {
    //   handleToast(
    //     false,
    //     err.response?.data?.message || "Failed to update user role"
    //   );
    // }

    // IMPORTANT → rethrow so UI catch works
    throw err;
  }
},

  async inviteUser(email) {
    try {
      const { data } = await axiosInstance.post(USER_API.INVITE_USER, {
        email,
        role: "user"
      });
      handleToast(true, data.message || "Invitation sent successfully");
      return data;
    } catch (err) {
      handleToast(false, err.response?.data?.message || "Failed to send invitation");
      throw err;
    }
  },

  async fetchInvites() {
    try {
      const { data } = await axiosInstance.get(USER_API.FETCH_INVITES);
      return data.data || [];
    } catch (err) {
      console.error("Failed to fetch invites", err);
      handleToast(false, err.response?.data?.message || "Failed to fetch invitation list");
      throw err;
    }
  },

  async cancelInvitation(email) {
    try {
      const { data } = await axiosInstance.delete(USER_API.CANCEL_INVITE, {
        data: { email }
      });
      handleToast(true, data.message || "Action successful");
      return data;
    } catch (err) {
      handleToast(false, err.response?.data?.message || "Action failed");
      throw err;
    }
  },

  /**
   * One user's subscription: what they have now, whether the store will charge
   * them again, and how they got here.
   *
   * Deliberately NOT toasted on failure — this loads inside a dialog that is
   * already open and shows its own inline error, and a red toast for a panel
   * the admin can simply close is noise.
   */
  async fetchSubscriptionStats(clientType = "web") {
    try {
      const res = await axiosInstance.get(ADMIN_STATS_API.SUBSCRIPTION_STATS, {
        headers: { "x-client-type": clientType },
      });
      return res.data.data || null;
    } catch (err) {
      console.error("Failed to fetch subscription stats", err);
      return null;
    }
  },

  async fetchSubscriptionTrend(from, to) {
    const res = await axiosInstance.get(ADMIN_STATS_API.SUBSCRIPTION_TREND, {
      params: { from, to },
      headers: { "x-client-type": "web" },
    });
    return res.data.data;
  },

  async fetchUserSubscription(userId, { refresh = false } = {}) {
    const { data } = await axiosInstance.get(
      ADMIN_USER_DATA_API.SUBSCRIPTION(userId),
      { params: refresh ? { refresh: true } : undefined },
    );
    return data.data;
  },
}
export default AdminService;


